import React, { useEffect, useRef, useState } from 'react';
import { Send, Mail, ArrowUpRight, Check } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { serviceOptions, whyChoose } from '../data/mock';
import MagneticButton from './MagneticButton';
import SceneHeader from './SceneHeader';

const Contact = () => {
  const { toast } = useToast();
  const ref = useRef(null);
  const [form, setForm] = useState({ name: '', email: '', company: '', service: '', message: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((x) => { if (x.isIntersecting) x.target.classList.add('in-view'); });
    }, { threshold: 0.1 });
    el.querySelectorAll('.reveal, .reveal-stagger').forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, []);

  const handleChange = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Missing info', description: 'Please fill required fields.' });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      toast({ title: 'Transmission received', description: 'We will respond within one business day.' });
      setForm({ name: '', email: '', company: '', service: '', message: '' });
    }, 900);
  };

  return (
    <section id="contact" ref={ref} className="relative py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SceneHeader
          chapter="06 / 06"
          subtitle="Contact"
          title={<>Let&apos;s build something <span className="font-serif neon-text">bold</span>.</>}
          description="Tell us about the world you want to build. We respond within 24 hours."
        />

        <div className="mt-20 grid lg:grid-cols-12 gap-10 reveal">
          {/* Left: contact stats */}
          <aside className="lg:col-span-4 flex flex-col gap-8">
            <div className="border-t border-white/10 pt-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Direct line</div>
              <a href="mailto:info@ozzlabs.com" data-cursor="hover" className="mt-3 inline-flex items-center gap-3 font-light text-2xl md:text-4xl chromatic-text tracking-[-0.02em] hover:text-cyan-300 transition-colors">
                <Mail className="w-5 h-5 text-cyan-300" strokeWidth={1.6} />
                info@ozzlabs.com
              </a>
            </div>

            <div className="border-t border-white/10 pt-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Operations</div>
              <div className="mt-3 font-light text-xl chromatic-text">24 / 7 — global</div>
              <p className="mt-2 text-sm text-white/50">Remote-first team across 5+ countries.</p>
            </div>

            <div className="border-t border-white/10 pt-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Why Ozzlabs</div>
              <ul className="mt-5 space-y-3">
                {whyChoose.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-0.5 w-4 h-4 rounded-full border border-cyan-400/40 flex items-center justify-center shrink-0">
                      <Check className="w-2.5 h-2.5 text-cyan-300" strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Right: form */}
          <div className="lg:col-span-8 relative rounded-2xl reflective overflow-hidden">
            <div className="absolute -top-40 -right-40 w-[28rem] h-[28rem] rounded-full bg-cyan-500/10 blur-3xl" />
            <form onSubmit={handleSubmit} className="relative p-8 md:p-12 grid md:grid-cols-2 gap-6">
              <div className="md:col-span-2 flex items-center justify-between pb-4 border-b border-white/10">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Transmission / New</div>
                <div className="font-mono text-[10px] text-white/30">ENCRYPTED</div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name" className="text-[10px] uppercase tracking-[0.3em] text-white/40">Full name</Label>
                <Input id="name" placeholder="Your name" value={form.name} onChange={handleChange('name')}
                  className="h-12 bg-transparent border-0 border-b border-white/10 rounded-none text-white placeholder:text-white/25 focus-visible:ring-0 focus-visible:border-cyan-400/60 px-0" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[10px] uppercase tracking-[0.3em] text-white/40">Email</Label>
                <Input id="email" type="email" placeholder="you@domain.com" value={form.email} onChange={handleChange('email')}
                  className="h-12 bg-transparent border-0 border-b border-white/10 rounded-none text-white placeholder:text-white/25 focus-visible:ring-0 focus-visible:border-cyan-400/60 px-0" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-[10px] uppercase tracking-[0.3em] text-white/40">Organisation</Label>
                <Input id="company" placeholder="Company" value={form.company} onChange={handleChange('company')}
                  className="h-12 bg-transparent border-0 border-b border-white/10 rounded-none text-white placeholder:text-white/25 focus-visible:ring-0 focus-visible:border-cyan-400/60 px-0" />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.3em] text-white/40">Discipline</Label>
                <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                  <SelectTrigger className="h-12 bg-transparent border-0 border-b border-white/10 rounded-none text-white data-[placeholder]:text-white/25 focus:ring-0 focus:border-cyan-400/60 px-0">
                    <SelectValue placeholder="Select a discipline" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0f] border-white/10 text-white">
                    {serviceOptions.map((opt) => (
                      <SelectItem key={opt} value={opt} className="focus:bg-cyan-500/10 focus:text-white">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="message" className="text-[10px] uppercase tracking-[0.3em] text-white/40">The brief</Label>
                <Textarea id="message" rows={4} placeholder="Tell us what you want to build…" value={form.message} onChange={handleChange('message')}
                  className="bg-transparent border-0 border-b border-white/10 rounded-none text-white placeholder:text-white/25 focus-visible:ring-0 focus-visible:border-cyan-400/60 px-0 resize-none" />
              </div>
              <div className="md:col-span-2 flex items-center justify-between pt-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/35">
                  Avg. reply — 4h during biz hours
                </div>
                <MagneticButton>
                  <button type="submit" disabled={loading} data-cursor="hover"
                    className="btn-primary inline-flex items-center gap-3 h-14 pl-7 pr-3 rounded-full text-[13px] uppercase tracking-[0.22em]">
                    {loading ? 'Transmitting…' : 'Send transmission'}
                    <span className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center">
                      {loading ? <Send className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
                    </span>
                  </button>
                </MagneticButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
